import { FindingsAlert, FindingsSelector } from '@app/icare/components/findings'
import { ADTeamConfig } from '@app/icare/entities/adTeam/config'
import { patientADTeamQueries } from '@app/icare/entities/adTeam/queries/patientADTeamQueries'
import { PatientADTeamEditDrawer } from '@app/icare/entities/patient/edits/PatientADTeamEditDrawer'
import {
  patientFindingsQueryOptions,
  selectByGroup,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import type { PatientADTeamRelation } from '@app/icare/entities/patient/schema/PatientADTeam'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Flex, Table } from 'antd'
import dayjs from 'dayjs'
import { FaPencil } from 'react-icons/fa6'
import { useRelationCommitNavigation } from '../-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/zustandigkeit/ad-team')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(patientADTeamQueries.forList(params.id)),
      queryClient.ensureQueryData(patientFindingsQueryOptions(params.id)),
    ]),
  component: RouteComponent,
})

function RouteComponent() {
  const { id: patientId } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: items = [] } = useSuspenseQuery(patientADTeamQueries.forList(patientId))

  const handleAfterCommitAdTeam = useRelationCommitNavigation<PatientADTeamRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: () =>
      navigate({
        to: '/patienten/$id/zustandigkeit/ad-team',
        params: { id: patientId },
      }),
    onInactiveEditNavigate: () => navigate({ to: '/patienten/$id/zustandigkeit/ad-team', params: { id: patientId } }),
  })
  return (
    <Flex vertical gap="small">
      <FindingsSelector selector={selectByGroup('adTeams')} children={f => <FindingsAlert findings={f} />} />

      <Table
        dataSource={items}
        rowKey="id"
        locale={{ emptyText: t`Keine AD-Teams ausgewählt` }}
        size="small"
        columns={[
          { title: t`Name`, render: (_, r) => ADTeamConfig.format.primary(r.to) },
          {
            title: t`Zuständig von`,
            render: (_, r) => (r.activeFrom ? dayjs(r.activeFrom).format('DD.MM.YYYY') : '-'),
          },
          { title: t`Zuständig bis`, render: (_, r) => (r.activeTo ? dayjs(r.activeTo).format('DD.MM.YYYY') : '-') },
          {
            key: 'actions',
            width: 80,
            render: (_, r) => (
              <Button
                type="text"
                size="small"
                icon={<FaPencil />}
                onClick={() =>
                  void PatientADTeamEditDrawer.show({
                    patientId,
                    relationId: r.id,
                    section: 'period',
                    onAfterCommit: handleAfterCommitAdTeam,
                  })
                }
              />
            ),
          },
        ]}
      />
    </Flex>
  )
}
